import { AppShell, LanguageSelector, type SeoConfig } from '@kkf/design'
import { LOGO_WIDE, LOGO_WIDE_WHITE } from '../data/shell'
import { useShellContent } from '../hooks/useShellContent'

export interface SiteShellProps {
  seo: SeoConfig
}

/**
 * The garage's chrome, in the reader's language.
 *
 * A component rather than the `<AppShell>` that used to sit in `App`'s route table: `App`
 * renders `LocaleProvider`, and a component cannot read a context it provides itself.
 *
 * `header.actions` carries the language chooser, and nothing else. Without it the catalogue
 * is unreachable: a reader could only get Mongolian by having asked their browser for it.
 *
 * **The cart badge and the search opener both came off on 2026-09-19.** The cart went with
 * the shop — there is nothing on this site to add to one. The search went for the reason the
 * Foundation's did: `SearchModal` only ever searched the page it was opened from, so it
 * promised a site search and delivered a filter. `search={false}` also stops the modal being
 * mounted, since the opener was the only thing that could reach it.
 *
 * **The ink tone is gone (2026-09-19).** The garage was the one dark-first app and is not
 * any more: the platform collapsed five palettes into one, and an app with a ground of its
 * own is not sharing a palette. The prop, and `.ul-header--ink` / `.ul-footer--ink` behind
 * it, are all still in the design system — a documented capability with a long write-up,
 * and the way back if the register ever returns. Nothing passes it today.
 *
 * What makes this app the garage is `sections/_garage.scss`, `sections/_adventure.scss` and
 * the three type tokens in `src/styles/_theme.scss`. None of those moved.
 */
export function SiteShell({ seo }: SiteShellProps) {
  const { nav, headerCta, footer, sidebar, bottomNav } = useShellContent()

  return (
    <AppShell
      logo={LOGO_WIDE}
      logoPale={LOGO_WIDE_WHITE}
      nav={nav}
      sidebar={sidebar}
      header={{
        cta: headerCta,
        /*
         * All ten of the garage's route patterns open into pale content, so the bar's
         * links have to darken on every one of them. `/blog` and `/blog/*` arrived with the
         * shared blog on 2026-09-21; the prefix form is the only one that covers a `:slug`.
         *
         * They did not need to before 2026-09-19. This app's heroes are photographs under a
         * scrim painted from `--ul-surface`; that scrim DARKENED the picture while the app
         * was dark-first, and lightens it now that the platform has one pale palette — so
         * white-at-rest links went white-on-near-white everywhere.
         *
         * `/privacy` and `/terms` are on the list because `LegalPage` dropped its
         * breadcrumb on 2026-09-19 and both pages now open on their own pale content. That
         * component is shared by all five apps, so the same line is in each of their shells.
         *
         * `/tours/*` joined them on 2026-09-21, when the last breadcrumb on the platform
         * came off. **It is still a list and not `plain: true`.** An app-wide boolean was
         * tried here first, and `/tours/:slug` — then the one page opening on a dark
         * photographic banner — is what it broke: ink navy on near-black, the same bug on
         * one route instead of nine. "Is the bar over something pale" is a per-route fact
         * even when the answer is the same for all eight, and the day a banner comes back
         * the fix is deleting one line rather than finding this note again.
         *
         * The `/*` suffix is a prefix match and is the only way to name a dynamic route.
         */
        plainRoutes: [
          '/',
          '/tours',
          '/tours/*',
          '/services',
          '/programmes',
          '/contact',
          '/blog',
          '/blog/*',
          '/privacy',
          '/terms',
        ],
        search: false,
        actions: <LanguageSelector />,
      }}
      footer={footer}
      bottomNav={bottomNav}
      seo={seo}
    />
  )
}
